import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaTrash,
  FaEdit,
  FaEye,
  FaPhoneAlt,
  FaFilter,
  FaWallet,
  FaPlus,
  FaTimes,
} from "react-icons/fa";
import AllUserListModal from "../../components/Modals/user/AllUserListModal";
import DeleteModal from "../../components/Modals/user/DeleteModal";
import WalletModal from "../../components/Modals/WalletModal";
import {
  ActiveuserList,
  userDetails,
  deleteUser,
  updateUserStatus,
  getUserWallet,
  addUserDepositAmount,
} from "../../api/endpoints";
import Swal from "../../utils/swalCompat";
import PageHeader from "../../components/PageHeader";

// ============================================================
// AddDepositModal Component
// ============================================================
const AddDepositModal = ({ show, setShow, userId, onSuccess }) => {
  const [amount, setAmount] = useState("");
  const [type, setType] = useState("DEPOSIT");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!amount || amount <= 0) {
      Swal.fire("Error", "Please enter a valid amount greater than 0", "error");
      return;
    }

    const confirmResult = await Swal.fire({
      title: "Are you sure?",
      html: `
        <div style="text-align: left; font-size: 16px;">
          <p><strong>User ID:</strong> ${userId}</p>
          <p><strong>Amount:</strong> ₹${Number(amount).toLocaleString()}</p>
          <p><strong>Type:</strong> ${type}</p>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#28a745",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Add it!",
      cancelButtonText: "Cancel",
    });

    if (!confirmResult.isConfirmed) return;

    try {
      setLoading(true);
      const res = await addUserDepositAmount({
        userId,
        amount: Number(amount),
        type: type,
      });
      console.log("Add Amount Response:", res);

      const successMessage =
        type === "DEPOSIT"
          ? `₹${amount} deposited successfully`
          : type === "BONUS"
          ? `₹${amount} bonus added successfully`
          : `₹${amount} penalty added successfully`;

      Swal.fire({
        icon: "success",
        title: "Success!",
        text: successMessage,
        timer: 2000,
        showConfirmButton: false,
      });

      setAmount("");
      setType("DEPOSIT");
      setShow(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error("Add Amount Error:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to add amount",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`modal fade ${show ? "show d-block" : ""}`}
      style={{ backgroundColor: show ? "rgba(0,0,0,0.5)" : "none" }}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Add Wallet Amount</h5>
            <button
              type="button"
              className="btn-close"
              onClick={() => setShow(false)}
            ></button>
          </div>
          <div className="modal-body">
            <div className="mb-3">
              <label className="form-label fw-bold">Amount (₹)</label>
              <input
                type="number"
                className="form-control form-control-lg"
                placeholder="e.g. 500"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                disabled={loading}
              />
            </div>

            <div className="mb-3">
              <label className="form-label fw-bold">Type</label>
              <select
                className="form-select form-select-lg"
                value={type}
                onChange={(e) => setType(e.target.value)}
                disabled={loading}
              >
                <option value="DEPOSIT">Deposit</option>
                <option value="BONUS">Bonus</option>
                <option value="PENALTY">Penalty</option>
              </select>
              <small className="text-muted mt-1 d-block">
                {type === "DEPOSIT" && "Add money to user's deposit balance"}
                {type === "BONUS" && "Add bonus amount to user's bonus balance"}
                {type === "PENALTY" &&
                  "Deduct penalty from user's winning balance"}
              </small>
            </div>
          </div>
          <div className="modal-footer">
            <button
              className="btn btn-secondary"
              onClick={() => setShow(false)}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              className="btn btn-success"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                ></span>
              ) : (
                `Add ${type}`
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// MAIN COMPONENT
// ============================================================
const ActiveUserList = () => {
  const navigate = useNavigate();

  // Modal + Data States
  const [showAllUserListModal, setShowAllUserListModal] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const [ActiveusersList, setActiveusersList] = useState([]);
  const [editData, setEditData] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [walletData, setWalletData] = useState({});
  const [deleteId, setDeleteId] = useState(null);

  // Filters + Pagination
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [kycStatus, setKycStatus] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [showFilter, setShowFilter] = useState(false);

  // Loading States
  const [loading, setLoading] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [walletLoading, setWalletLoading] = useState(false);
  const [fetchingUserId, setFetchingUserId] = useState(null); // For Edit button loader

  // Add Deposit Modal
  const [showAddDepositModal, setShowAddDepositModal] = useState(false);
  const [depositUserId, setDepositUserId] = useState(null);

  // ============================================================
  // FETCH USER LIST
  // ============================================================
  const activeuserList = async () => {
    try {
      setLoading(true);
      const params = { page, limit };

      if (search.trim()) params.search = search.trim();
      if (status) params.status = status;
      if (kycStatus) params.kycStatus = kycStatus;

      console.log("PARAMS", params);
      const res = await ActiveuserList(params);
      console.log("Active User List Response:", res);

      const responseData = res.data?.data || res.data;

      setActiveusersList(responseData.users || []);
      setTotalPages(responseData.pagination?.totalPages || 1);
      setTotalItems(responseData.pagination?.totalItems || 0);

      if (responseData.pagination?.page) {
        setPage(responseData.pagination.page);
      }
    } catch (error) {
      console.log(error);
      Swal.fire("Error", "Failed to fetch active users", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    activeuserList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, status, kycStatus, page]);

  // ============================================================
  // ✅ HANDLE VIEW — Navigate to page
  // ============================================================
  const handleViewUserDetails = (userId) => {
    console.log("➡️ Navigating to /user-details/" + userId);
    navigate(`/user-details/${userId}`);
  };

  // ============================================================
  // ✅ HANDLE EDIT — Opens Modal with GET /admin/user-details/:id
  // ============================================================
  const handleEdit = async (userId) => {
    try {
      setFetchingUserId(userId);
      setShowAllUserListModal(false);
      setEditData(null);

      const res = await userDetails(userId);
      console.log("✅ EDIT USER DATA:", res);
      console.log("✅ URL hit: /admin/user-details/" + userId);

      const userData =
        res?.data?.data ||
        res?.data?.user ||
        res?.user ||
        res?.data ||
        res;

      console.log("✅ Extracted user (EDIT):", userData);

      setEditData(userData);
      setViewMode(false);

      setTimeout(() => {
        setShowAllUserListModal(true);
      }, 50);
    } catch (error) {
      console.error("❌ Edit error:", error);
      Swal.fire("Error", "Failed to fetch user details", "error");
    } finally {
      setFetchingUserId(null);
    }
  };

  // ============================================================
  // DELETE
  // ============================================================
  const handleDelete = async () => {
    try {
      await deleteUser({ userId: deleteId });
      setShowDeleteModal(false);
      activeuserList();
      Swal.fire("Deleted!", "User has been deleted successfully.", "success");
    } catch (error) {
      console.log("DELETE ERROR:", error);
      Swal.fire("Error", "Failed to delete user", "error");
    }
  };

  // ============================================================
  // STATUS TOGGLE
  // ============================================================
  const handleStatusToggle = async (userId, currentStatus) => {
    const newStatus = currentStatus === "ACTIVE" ? "INACTIVE" : "ACTIVE";

    try {
      setTogglingId(userId);
      setLoading(true);

      const response = await updateUserStatus({
        userId: userId,
        status: newStatus,
      });

      console.log("Status Update Response:", response);
      activeuserList();
    } catch (error) {
      console.log("STATUS UPDATE ERROR:", error);
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to update user status",
        "error"
      );
    } finally {
      setLoading(false);
      setTogglingId(null);
    }
  };

  // ============================================================
  // WALLET MODAL
  // ============================================================
  const openWalletModal = async (user) => {
    try {
      setWalletLoading(true);
      const response = await getUserWallet({ userId: user._id });
      console.log("Wallet API Response:", response);

      const walletDataFromAPI = response.data || response;

      setWalletData({
        fullName: user.fullName || "N/A",
        mobile: user.mobile || "N/A",
        walletBalance:
          (walletDataFromAPI.depositBalance || 0) +
          (walletDataFromAPI.winningBalance || 0) +
          (walletDataFromAPI.bonusBalance || 0) +
          (walletDataFromAPI.cashbackBalance || 0),
        depositBalance: walletDataFromAPI.depositBalance || 0,
        winningBalance: walletDataFromAPI.winningBalance || 0,
        bonusBalance: walletDataFromAPI.bonusBalance || 0,
        cashbackBalance: walletDataFromAPI.cashbackBalance || 0,
        lockedBalance: walletDataFromAPI.lockedBalance || 0,
        createdAt: walletDataFromAPI.createdAt || user.createdAt || new Date(),
        updatedAt: walletDataFromAPI.updatedAt || new Date(),
        transactions: walletDataFromAPI.transactions || [],
      });

      setShowWalletModal(true);
    } catch (error) {
      console.error("Wallet fetch error:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Could not fetch wallet data",
      });
    } finally {
      setWalletLoading(false);
    }
  };

  const handleDepositSuccess = () => {
    activeuserList();
  };

  // ============================================================
  // PAGINATION
  // ============================================================
  const goToPage = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) setPage(pageNumber);
  };

  const goToPreviousPage = () => {
    if (page > 1) setPage(page - 1);
  };

  const goToNextPage = () => {
    if (page < totalPages) setPage(page + 1);
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisible = 5;

    let startPage = Math.max(1, page - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  const clearFilters = () => {
    setSearch("");
    setStatus("");
    setKycStatus("");
    setPage(1);
  };

  const hasActiveFilters = search || status || kycStatus;

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div className="lead-page">
      <PageHeader
        title="Active User List"
      />
      <div className="card mb-4 card-light">
        <div className="card-header d-flex justify-content-between align-items-center">
          <div className="d-flex gap-2 align-items-center">
            {showFilter && (
              <div className="d-flex gap-2 align-items-center flex-wrap">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search User..."
                  value={search}
                  onChange={(e) => {
                    setPage(1);
                    setSearch(e.target.value);
                  }}
                  style={{ width: "180px" }}
                />

                <select
                  className="form-select"
                  value={status}
                  onChange={(e) => {
                    setPage(1);
                    setStatus(e.target.value);
                  }}
                  style={{ width: "140px" }}
                >
                  <option value="">All Status</option>
                  <option value="ACTIVE">Active</option>
                  <option value="INACTIVE">Inactive</option>
                </select>

                <select
                  className="form-select"
                  value={kycStatus}
                  onChange={(e) => {
                    setPage(1);
                    setKycStatus(e.target.value);
                  }}
                  style={{ width: "140px" }}
                >
                  <option value="">All KYC</option>
                  <option value="VERIFIED">Verified</option>
                  <option value="PENDING">Pending</option>
                  <option value="REJECTED">Rejected</option>
                  <option value="NOT_SUBMITTED">Not Submitted</option>
                </select>

                {hasActiveFilters && (
                  <button
                    className="btn btn-outline-danger"
                    onClick={clearFilters}
                    title="Clear all filters"
                  >
                    <FaTimes /> Clear
                  </button>
                )}
              </div>
            )}
            <button
              className={`btn ${showFilter ? "btn-primary" : "btn-dark"}`}
              onClick={() => setShowFilter(!showFilter)}
            >
              <FaFilter />
              {showFilter ? " Hide" : " Filter"}
            </button>
          </div>
        </div>

        <div className="card-body">
          <div className="mb-3 d-flex justify-content-between align-items-center flex-wrap">
            <div>
              <span className="text-muted">
                Showing {ActiveusersList.length} of {totalItems} users
              </span>
              {hasActiveFilters && (
                <span className="badge bg-info ms-2">Filters Applied</span>
              )}
            </div>
            <div>
              <span className="text-muted">
                Page {page} of {totalPages}
              </span>
            </div>
          </div>

          {/* Active Filters Display */}
          {hasActiveFilters && (
            <div className="mb-3 d-flex gap-2 flex-wrap">
              {search && (
                <span className="badge bg-secondary d-flex align-items-center gap-1">
                  Search: {search}
                  <button
                    className="btn btn-sm p-0 text-white"
                    onClick={() => {
                      setSearch("");
                      setPage(1);
                    }}
                    style={{ background: "none", border: "none" }}
                  >
                    <FaTimes />
                  </button>
                </span>
              )}
              {status && (
                <span className="badge bg-secondary d-flex align-items-center gap-1">
                  Status: {status}
                  <button
                    className="btn btn-sm p-0 text-white"
                    onClick={() => {
                      setStatus("");
                      setPage(1);
                    }}
                    style={{ background: "none", border: "none" }}
                  >
                    <FaTimes />
                  </button>
                </span>
              )}
              {kycStatus && (
                <span className="badge bg-secondary d-flex align-items-center gap-1">
                  KYC: {kycStatus}
                  <button
                    className="btn btn-sm p-0 text-white"
                    onClick={() => {
                      setKycStatus("");
                      setPage(1);
                    }}
                    style={{ background: "none", border: "none" }}
                  >
                    <FaTimes />
                  </button>
                </span>
              )}
            </div>
          )}

          <div className="table-responsive">
            <table className="table align-middle">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Mobile</th>
                    <th>Level</th>
                  <th>Referral</th>
            
                  <th>KYC</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading && ActiveusersList.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : ActiveusersList.length > 0 ? (
                  ActiveusersList.map((item, index) => (
                    <tr key={item._id}>
                      <td>{(page - 1) * limit + index + 1}</td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          {item.profileImage ? (
                            <img
                              src={item.profileImage}
                              width="35"
                              height="35"
                              className="rounded-circle"
                              alt=""
                            />
                          ) : (
                            <div
                              className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center"
                              style={{
                                width: "35px",
                                height: "35px",
                              }}
                            >
                              {item.fullName?.charAt(0) || "U"}
                            </div>
                          )}
                          <span>{item.fullName || "N/A"}</span>
                        </div>
                      </td>
                      <td>
                        <FaPhoneAlt size={12} /> {item.mobile}
                      </td>
                      <td>
                        <span className="badge bg-info">
                          {item.vipLevel}
                        </span>
                      </td>
                      <td>
                        <span className="badge bg-info">
                          {item.referralCode}
                        </span>
                      </td>
                      {/* <td>
                        <span className="badge bg-success">
                          {item.simplePin || "N/A"}
                        </span>
                      </td> */}
                      <td>
                        <span
                          className={`badge ${
                            item.isKycVerified === "VERIFIED"
                              ? "bg-success"
                              : item.isKycVerified === "PENDING"
                              ? "bg-warning"
                              : item.isKycVerified === "REJECTED"
                              ? "bg-danger"
                              : "bg-secondary"
                          }`}
                        >
                          {item.isKycVerified === "VERIFIED"
                            ? "Verified"
                            : item.isKycVerified === "PENDING"
                            ? "Pending"
                            : item.isKycVerified === "REJECTED"
                            ? "Rejected"
                            : item.isKycVerified || "N/A"}
                        </span>
                      </td>
                      <td>
                        <div className="toggle-switch-container">
                          <label className="toggle-switch">
                            <input
                              type="checkbox"
                              checked={item.status === "ACTIVE"}
                              onChange={() =>
                                handleStatusToggle(item._id, item.status)
                              }
                              disabled={togglingId === item._id || loading}
                            />
                            <span className="toggle-slider"></span>
                          </label>
                        </div>
                      </td>
                      <td>{new Date(item.createdAt).toLocaleDateString()}</td>
                      <td>
                        <div className="d-flex gap-2">
                          {/* ✅ Add Wallet Amount */}
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => {
                              setDepositUserId(item._id);
                              setShowAddDepositModal(true);
                            }}
                            title="Add Wallet Amount"
                          >
                            <FaPlus /> ₹
                          </button>

                          {/* ✅ Wallet */}
                          <button
                            className="btn btn-sm btn-info custum_info"
                            onClick={() => openWalletModal(item)}
                            disabled={walletLoading}
                            title="View Wallet"
                          >
                            {walletLoading ? (
                              <span
                                className="spinner-border spinner-border-sm"
                                role="status"
                                aria-hidden="true"
                              ></span>
                            ) : (
                              <FaWallet />
                            )}
                          </button>

                          {/* ✅ VIEW BUTTON — Navigate to page */}
                          <button
                            className="btn btn-sm btn-info custum_primary"
                            onClick={() => handleViewUserDetails(item._id)}
                            title="View User Details"
                          >
                            <FaEye />
                          </button>

                          {/* ✅ EDIT BUTTON — Opens Modal */}
                          {/* <button
                            className="btn btn-sm btn-info custum_edit text-white"
                            onClick={() => handleEdit(item._id)}
                            disabled={fetchingUserId === item._id}
                            title="Edit User"
                          >
                            {fetchingUserId === item._id ? (
                              <span
                                className="spinner-border spinner-border-sm"
                                role="status"
                              ></span>
                            ) : (
                              <FaEdit />
                            )}
                          </button> */}

                          {/* ✅ DELETE */}
                          <button
                            className="btn btn-sm btn-info custum_danger"
                            onClick={() => {
                              setDeleteId(item._id);
                              setShowDeleteModal(true);
                            }}
                            title="Delete User"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center">
                      {hasActiveFilters
                        ? "No active users found matching your filters"
                        : "No Active Users Found"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-center align-items-center gap-2 mt-4">
              <button
                className="btn btn-outline-secondary"
                onClick={goToPreviousPage}
                disabled={page <= 1 || loading}
              >
                Previous
              </button>

              <div className="d-flex gap-1">
                {getPageNumbers().map((pageNum) => (
                  <button
                    key={pageNum}
                    className={`btn ${
                      pageNum === page ? "btn-primary" : "btn-outline-primary"
                    }`}
                    onClick={() => goToPage(pageNum)}
                    disabled={loading}
                    style={{ minWidth: "40px" }}
                  >
                    {pageNum}
                  </button>
                ))}
              </div>

              <button
                className="btn btn-outline-secondary"
                onClick={goToNextPage}
                disabled={page >= totalPages || loading}
              >
                Next
              </button>
            </div>
          )}

          <div className="d-flex justify-content-center mt-2">
            <small className="text-muted">
              Showing {ActiveusersList.length} of {totalItems} users | Page{" "}
              {page} of {totalPages}
            </small>
          </div>
        </div>
      </div>

      {/* ===================== MODALS ===================== */}
      <AddDepositModal
        show={showAddDepositModal}
        setShow={setShowAddDepositModal}
        userId={depositUserId}
        onSuccess={handleDepositSuccess}
      />

      {/* ✅ EDIT MODAL — only for Edit, not View */}
      <AllUserListModal
        show={showAllUserListModal}
        setShow={setShowAllUserListModal}
        editData={editData}
        viewMode={viewMode}
      />

      <DeleteModal
        show={showDeleteModal}
        setShow={setShowDeleteModal}
        onConfirm={handleDelete}
        title="Delete User?"
        message="Are you sure you want to delete this user?"
      />

      <WalletModal
        show={showWalletModal}
        setShow={setShowWalletModal}
        editData={walletData}
      />
    </div>
  );
};

export default ActiveUserList;